import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, SpecraDataTable } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertCircle, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDuration } from '../../lib/tool-configs';
import { useRemoteCatalog } from '../../lib/tool-configs/remote';
import { describeToolStep } from './toolProgressSummary';
import './ToolProgress.css';
function StepIcon({ status }) {
    if (status === 'Working') return /*#__PURE__*/ _jsx(Loader2, {
        "aria-hidden": true,
        className: "size-4 shrink-0 animate-spin motion-reduce:animate-none text-primary"
    });
    if (status === 'Complete') return /*#__PURE__*/ _jsx(CheckCircle2, {
        "aria-hidden": true,
        className: "size-4 shrink-0 text-primary"
    });
    if (status === 'Failed') return /*#__PURE__*/ _jsx(AlertCircle, {
        "aria-hidden": true,
        className: "size-4 shrink-0 text-destructive"
    });
    return /*#__PURE__*/ _jsx(Circle, {
        "aria-hidden": true,
        className: "size-4 shrink-0 text-muted-foreground"
    });
}
function StepName({ part, number, step }) {
    return /*#__PURE__*/ _jsxs("span", {
        className: "flex items-start gap-2 min-w-0",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-xs tabular-nums text-muted-foreground pt-0.5",
                children: number
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "block font-medium text-foreground break-words",
                        children: step.name
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "mt-1 flex items-center gap-1.5 text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx(StepIcon, {
                                status: step.status
                            }),
                            step.status,
                            part.duration !== undefined ? ` · ${formatDuration(part.duration)}` : ''
                        ]
                    })
                ]
            })
        ]
    });
}
function StepEvidence({ part, step }) {
    const evidence = JSON.stringify({
        input: part.input,
        result: part.output
    }, null, 2);
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-md border border-border bg-background p-3 space-y-3 text-xs",
        children: [
            step.caveats.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium mb-1",
                        children: "Limits to this result"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "list-disc pl-4 space-y-1",
                        children: step.caveats.map((text, i)=>/*#__PURE__*/ _jsx("li", {
                                children: text
                            }, `${i}-${text}`))
                    })
                ]
            }),
            step.sourceUrl && /*#__PURE__*/ _jsxs("a", {
                href: step.sourceUrl,
                target: "_blank",
                rel: "noopener noreferrer",
                className: "block text-primary underline break-all",
                children: [
                    "Open source: ",
                    new URL(step.sourceUrl).hostname
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "Inputs and returned evidence"
            }),
            evidence.length > 12000 && /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "Showing the first 12,000 characters of the returned evidence."
            }),
            /*#__PURE__*/ _jsx("pre", {
                className: "max-h-72 overflow-auto whitespace-pre-wrap break-all text-muted-foreground",
                children: evidence.slice(0, 12000)
            })
        ]
    });
}
export function ToolProgress({ parts, active = false }) {
    useRemoteCatalog((state)=>state.configs);
    // An explicit user choice persists as streamed rows arrive and the request finishes.
    const [userOpen, setUserOpen] = useState();
    const [decisions, setDecisions] = useState({});
    useEffect(()=>{
        const onDecision = (event)=>{
            const { toolCallId, decision } = event.detail ?? {};
            if (toolCallId && (decision === 'accepted' || decision === 'rejected')) {
                setDecisions((previous)=>({
                        ...previous,
                        [toolCallId]: decision
                    }));
            }
        };
        window.addEventListener('shanks-document-proposal-decided', onDecision);
        return ()=>window.removeEventListener('shanks-document-proposal-decided', onDecision);
    }, []);
    parts = parts.map((part)=>part.toolCallId && decisions[part.toolCallId] && part.output && typeof part.output === 'object' ? {
            ...part,
            output: {
                ...part.output,
                approvalState: decisions[part.toolCallId]
            }
        } : part);
    const steps = parts.map((part)=>describeToolStep(part, active));
    const returned = parts.filter((part)=>part.output !== undefined && part.output !== null).length;
    const failed = steps.filter((step)=>step.status === 'Failed').length;
    const approval = steps.some((step)=>step.status === 'Needs approval');
    const needsAttention = steps.some((step)=>[
            'Partial',
            'Needs verification',
            'Not completed'
        ].includes(step.status));
    const current = steps.find((step)=>step.status === 'Working');
    const title = active ? current ? `Working · ${current.name}` : 'Working through your request' : approval ? 'Your review is needed' : failed ? 'Finished with issues' : needsAttention ? 'Results and limits' : 'Work completed';
    return /*#__PURE__*/ _jsx("div", {
        className: "tool-progress min-w-0 my-3",
        "data-testid": Testids.assistant.toolProgress,
        children: /*#__PURE__*/ _jsx(Accordion, {
            type: "single",
            collapsible: true,
            value: userOpen ?? (active || failed > 0 || approval) ? 'progress' : '',
            onValueChange: (value)=>setUserOpen(value === 'progress'),
            className: "rounded-lg border border-border bg-muted/30 overflow-hidden",
            children: /*#__PURE__*/ _jsxs(AccordionItem, {
                value: "progress",
                className: "border-0",
                children: [
                    /*#__PURE__*/ _jsx(AccordionTrigger, {
                        className: "px-4 py-3 gap-3 hover:no-underline",
                        children: /*#__PURE__*/ _jsxs("span", {
                            className: "flex flex-1 items-center gap-2 min-w-0 text-left",
                            children: [
                                /*#__PURE__*/ _jsx(StepIcon, {
                                    status: active ? 'Working' : failed ? 'Failed' : needsAttention || approval ? 'Needs verification' : 'Complete'
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "min-w-0",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "block font-medium",
                                            children: title
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "block mt-0.5 text-xs font-normal text-muted-foreground",
                                            children: [
                                                `${returned} of ${parts.length} ${parts.length === 1 ? 'step' : 'steps'} returned`,
                                                failed ? ` · ${failed} failed` : ''
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(AccordionContent, {
                        className: "pb-0",
                        children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                            framed: false,
                            rows: parts.map((part, index)=>({
                                    part,
                                    number: index + 1,
                                    step: steps[index]
                                })),
                            getRowId: (row)=>row.part.toolCallId || row.number,
                            rowTestId: Testids.assistant.toolProgressStep,
                            getRowProps: ()=>({
                                    className: 'tool-progress-grid gap-3 px-4 py-3'
                                }),
                            tableProps: {
                                'aria-label': 'Request progress',
                                className: 'w-full text-sm'
                            },
                            getExpandToggleLabel: (row, expanded)=>`Step ${row.number}: ${row.step.name}. ${expanded ? 'Hide' : 'Show'} details`,
                            renderExpanded: (row)=>/*#__PURE__*/ _jsx(StepEvidence, {
                                    ...row
                                }),
                            columns: [
                                {
                                    id: 'step',
                                    header: 'Step',
                                    className: 'min-w-0',
                                    cell: (row)=>/*#__PURE__*/ _jsx(StepName, {
                                            ...row
                                        })
                                },
                                {
                                    id: 'purpose',
                                    header: 'Purpose',
                                    className: 'min-w-0 break-words text-muted-foreground',
                                    cell: (row)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "tool-progress-label",
                                                    children: "Purpose"
                                                }),
                                                row.step.purpose
                                            ]
                                        })
                                },
                                {
                                    id: 'result',
                                    header: 'Result',
                                    className: 'min-w-0 break-words text-foreground',
                                    cell: (row)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "tool-progress-label",
                                                    children: "Result"
                                                }),
                                                row.step.result
                                            ]
                                        })
                                }
                            ]
                        })
                    })
                ]
            })
        })
    });
}
